import type { JSTViewJobTarget } from "./JSTLandingScreen";
import type { JSTSearchOverlayArgs } from "./JSTSearchOverlayArgs";

export type ViewJob = {
  type: "ViewJob";
  /**
   * See JSTLandingScreen.JSTViewJobTarget
   */
  target: JSTViewJobTarget;
};

export type IndeedApply = {
  type: "IndeedApply";
  url: string;
};

export type Onboarding = {
  type: "Onboarding";
  /**
   * See JSTOnboardingParams
   */
  params: JSTOnboardingParams;
};

export type JSTOnboardingParams = {
  /** Unique identifier for the onboarding experience. */
  experienceKey: string;
  /** Dictionary of data for the onboarding experience. */
  experienceData: Record<string, unknown>;
  /** Whether to skip exemption checks. */
  skipExempt: boolean;
  /** (Optional) Origin identifier. (Tracking purpose) */
  origin?: string;
  /** (Optional) Source of this navigation action. */
  from?: string;
};

export type JSTFileInfo = {
  uri: string;
  fileName: string;
};

export type DocumentPreview = {
  type: "DocumentPreview";
  fileInfo: JSTFileInfo;
};

export type SearchOverlay = {
  type: "SearchOverlay";
  args: JSTSearchOverlayArgs;
  origin: string | null;
};

/**
 * Ai Recruiter modal - presents the aiRecruiter URL in a modal webview.
 */
export type AiRecruiter = {
  type: "AiRecruiter";
  url: string;
};

/**
 * Schedule Apply onboarding modal - presents the Schedule Apply consent flow.
 * Resolves with COMPLETED (user consented) or CANCELLED (user declined).
 */
export type ScheduleApplyOnboarding = {
  type: "ScheduleApplyOnboarding";
  jobKey: string;
};

/**
 * Schedule Apply prefilling modal — presented in the phase A (synchronous)
 * flow after the user taps "Prefill your application" in ViewJob. Hosts the
 * `GhostwriterPrefillingRoot` screen which polls the application status and
 * either auto-routes to the review screen or lets the user dismiss back to
 * ViewJob.
 */
export type ScheduleApplyPrefilling = {
  type: "ScheduleApplyPrefilling";
  jobKey: string;
};

/**
 * Interview Scheduling modal - presents the interview scheduling URL in a modal webview.
 * Resolves with SUCCESS or CANCEL.
 */
export type InterviewScheduling = {
  type: "InterviewScheduling";
  url: string;
  preferFullScreenModal: boolean;
  title: string | null;
};

export type Login = {
  type: "Login";
  isRegistration: boolean;
  isContinueWithGoogle: boolean;
};

export type AceConversation = {
  type: "AceConversation";
  starterMessage?: string | null;
  agentType?: string | null;
  conversationId?: string | null;
  sourceContext?: string | null;
};

/**
 * JP Onboarding modal — presents the JP web onboarding (JSO) flow in a modal webview.
 * The native layer derives the JSO URL from the current site configuration.
 * The web frontend signals completion by calling `jsma-bridge.dismissModalWithWebView(result)`.
 */
export type JpOnboarding = {
  type: "JpOnboarding";
};

/**
 * Registration Promo modal — presents the login/registration screen.
 * Resolves with cancel, signIn, or register.
 */
export type RegPromo = {
  type: "RegPromo";
  dontDismissOnCancel: boolean;
};

/**
 * Company page in a native modal webview (see JSTLandingScreen.companyPage for full-screen push).
 */
export type CompanyPageModal = {
  type: "CompanyPage";
  targetUrl: string;
  shareUrl?: string | null;
  shareMessage?: string | null;
  shareTk?: string | null;
  preferFullScreenModal: boolean;
  title: string | null;
};

/**
 * CRAVE document submission modal — presents the credential-required URL in a
 * modal webview. Resolves with `{ result: 'SUBMITTED' }` if the hosted page
 * called `dismissModalWithWebView({ result: 'submitted' })`, otherwise
 * `{ result: 'CLOSED' }`.
 */
export type CraveDocSubmission = {
  type: "CraveDocSubmission";
  url: string;
};

/**
 * Resume Builder modal — presents the resume builder completion route in a
 * modal webview. The native layer derives the URL from the profile domain.
 * Resolves with `{ result: 'COMPLETED' | 'CANCELLED' | 'ERROR' }`.
 */
export type ResumeBuilder = {
  type: "ResumeBuilder";
  preferFullScreenModal: boolean;
};

export type JSTCraveDocSubmissionResult = {
  result: "SUBMITTED" | "CLOSED";
};

/**
 * Result of the Schedule Apply prefilling modal.
 *
 * The prefilling modal never performs `navigateTo` from inside its own RN
 * root tag — iOS forbids push navigation from a presented view controller
 * that has not been dismissed. All post-dismiss routing is the presenter's
 * (ViewJob's) responsibility; the values below describe the intent the
 * modal is signalling.
 *
 * - `'DISMISSED'`: user tapped the X close button.
 * - `'RETURN_TO_JOBS'`: user tapped the "Return to jobs" CTA — presenter
 *   should close ViewJob and navigate to Home.
 * - `'OPEN_REVIEW'`: polling reached review-ready and the modal
 *   auto-dismissed — presenter should `navigateTo` the application review
 *   landing screen (presenter already knows the applicationId).
 */
export type JSTScheduleApplyPrefillingResult = {
  result: "DISMISSED" | "RETURN_TO_JOBS" | "OPEN_REVIEW";
};

/**
 * External Apply modal — presents the external apply URL in a modal webview.
 * The external apply webview does not share cookies or other info with
 * standard Indeed webviews. Resolves with `{ result: 'CLOSED' }` when the
 * webview is closed.
 */
export type ExternalApply = {
  type: "ExternalApply";
  url: string;
};

export type JSTExternalApplyResult = {
  result: "CLOSED";
};

export type JSTOpenModalResult = {
  result: string;
};

export type JSTViewJobResult = {
  result: string;
};

export type JSTAceConversationResult = {
  result: "CLOSED";
  conversationId?: string | null;
};

export type SourcingOptIn = {
  type: "SourcingOptIn";
};

/**
 * A WebView route resolved and retained by the native navigator.
 *
 * This type is only available as a native pass through return type. The url is
 * always embedded and not configurable by clients.
 */
export type WebView = {
  type: "WebView";
};

export type JSTSourcingOptInResult = {
  result: "CLOSED";
};

export type JSTWebViewResult = {
  result: "CLOSED";
};

export type JSTModalScreen =
  | ViewJob
  | IndeedApply
  | Onboarding
  | DocumentPreview
  | SearchOverlay
  | AiRecruiter
  | ScheduleApplyOnboarding
  | ScheduleApplyPrefilling
  | InterviewScheduling
  | CompanyPageModal
  | RegPromo
  | JpOnboarding
  | Login
  | AceConversation
  | CraveDocSubmission
  | ExternalApply
  | ResumeBuilder
  | SourcingOptIn
  | WebView;

export const JSTModalScreen = {
  /**
   * Creates a ViewJob landing screen
   */
  viewJob(target: JSTViewJobTarget): ViewJob {
    return {
      type: "ViewJob",
      target,
    };
  },

  /**
   * Creates an Indeed Apply modal screen
   */
  indeedApply(url: string): IndeedApply {
    return {
      type: "IndeedApply",
      url,
    };
  },

  /**
   *  Opens the native retriggerable onboarding modal.
   */
  onboarding(params: JSTOnboardingParams): Onboarding {
    return {
      type: "Onboarding",
      params: params,
    };
  },

  /**
   * Creates a DocumentPreview modal screen
   */
  documentPreview(fileInfo: JSTFileInfo): DocumentPreview {
    return {
      type: "DocumentPreview",
      fileInfo,
    };
  },

  /**
   * Creates a SearchOverlay modal screen
   */
  searchOverlay(args: JSTSearchOverlayArgs, origin: string | null): SearchOverlay {
    return {
      type: "SearchOverlay",
      args,
      origin,
    };
  },

  /**
   * Creates an Ai Recruiter modal screen - presents the URL in a modal webview.
   */
  aiRecruiter(url: string): AiRecruiter {
    return {
      type: "AiRecruiter",
      url,
    };
  },

  /**
   * Opens the Schedule Apply onboarding consent flow as a modal.
   */
  scheduleApplyOnboarding(jobKey: string): ScheduleApplyOnboarding {
    return {
      type: "ScheduleApplyOnboarding",
      jobKey,
    };
  },

  /**
   * Opens the Schedule Apply prefilling progress screen as a modal.
   * Phase A only — the modal is dismissed either by the user (via
   * the in-screen close affordance) or automatically once the
   * application reaches the review-ready state.
   */
  scheduleApplyPrefilling(jobKey: string): ScheduleApplyPrefilling {
    return {
      type: "ScheduleApplyPrefilling",
      jobKey,
    };
  },

  /**
   * Opens the Interview Scheduling modal in a webview.
   */
  interviewScheduling(
    url: string,
    preferFullScreenModal: boolean,
    title: string | null,
  ): InterviewScheduling {
    return {
      type: "InterviewScheduling",
      url,
      preferFullScreenModal,
      title,
    };
  },

  /**
   * Opens a company page URL in a native modal webview.
   */
  companyPage(
    targetUrl: string,
    extras?: {
      shareUrl?: string | null;
      shareMessage?: string | null;
      shareTk?: string | null;
      preferFullScreenModal?: boolean;
      title?: string | null;
    },
  ): CompanyPageModal {
    const e = extras ?? {};
    return {
      type: "CompanyPage",
      targetUrl,
      shareUrl: e.shareUrl ?? null,
      shareMessage: e.shareMessage ?? null,
      shareTk: e.shareTk ?? null,
      preferFullScreenModal: e.preferFullScreenModal ?? false,
      title: e.title ?? null,
    };
  },

  /**
   * Opens the Passport login/registration modal.
   */
  login(isRegistration: boolean, isContinueWithGoogle: boolean): Login {
    return {
      type: "Login",
      isRegistration,
      isContinueWithGoogle,
    };
  },

  aceConversation(params: {
    starterMessage?: string | null;
    agentType?: string | null;
    conversationId?: string | null;
    sourceContext?: string | null;
  }): AceConversation {
    return {
      type: "AceConversation",
      ...params,
    };
  },

  /**
   * Opens the Registration Promo login/registration screen as a modal.
   */
  regPromo(dontDismissOnCancel = false): RegPromo {
    return {
      type: "RegPromo",
      dontDismissOnCancel,
    };
  },

  /**
   * Opens the JP web onboarding (JSO) flow as a modal webview.
   */
  jpOnboarding(): JpOnboarding {
    return {
      type: "JpOnboarding",
    };
  },

  /**
   * Opens the CRAVE credential-required URL in a modal webview.
   */
  craveDocSubmission(url: string): CraveDocSubmission {
    return {
      type: "CraveDocSubmission",
      url,
    };
  },

  /**
   * Opens an external apply URL in a modal external webview.
   * The external apply webview does not share cookies or other info with standard Indeed webviews.
   */
  externalApply(url: string): ExternalApply {
    return {
      type: "ExternalApply",
      url,
    };
  },

  /**
   * Opens the Resume Builder completion route as a modal webview.
   * The native layer derives the URL from the profile domain.
   */
  resumeBuilder(preferFullScreenModal: boolean): ResumeBuilder {
    return {
      type: "ResumeBuilder",
      preferFullScreenModal,
    };
  },

  sourcingOptIn(): SourcingOptIn {
    return { type: "SourcingOptIn" };
  },
};
